/// <amd-module name="typeahead/features/DropDownGeometryOptimizer" />

/// <amd-dependency path="jquery" name="$"/>

/// <reference path="../../typings/thirdparty/jquery/jquery.d.ts" />

import {Experiment} from "typeahead/core/Experiment";
import {IExperimentalFeature} from "typeahead/core/IExperimentalFeature";
import * as ExecutionHelper from "typeahead/core/plugins/ExecutionHelper";
import {IDropDownHideHandler} from "typeahead/core/presentation/IDropDownHideHandler";
import {IDropDownShowHandler} from "typeahead/core/presentation/IDropDownShowHandler";
import * as StripingConfiguration from "typeahead/core/StripingConfiguration";
import {IStripingConfiguration} from "typeahead/core/StripingConfiguration";

const configuration: IStripingConfiguration[] = [
    {
        clients: ["Flights.Search"],
        experiment: {
            id: 15336,
            name: "FSRP_uTA_OptimizeDropdownGeometry",
            value: 1,
        },
    },
];

export class DropDownGeometryOptimizer implements IDropDownHideHandler<undefined>, IDropDownShowHandler<undefined>,
        IExperimentalFeature {
    // @baseSpacingHorz as defined in uitoolkit; see uitoolkit/.../mixins.less (http://go/iuo39gal)
    private static baseSpacingHorz: number = 12;
    private static maxAvailableMediaWidthPx = 770; // Width of a small lablet as defined in UI Toolkit
    private static resizeEventTypes: string = "orientationchange resize";

    private static optimizeDropDownGeometry($dropDownElement: JQuery, $inputElement: JQuery): void {
        const availableMediaWidthPx: number =
            4 + document.body.clientWidth - DropDownGeometryOptimizer.baseSpacingHorz * 2;
        const maxDropDownWidthPx: number =
            4 + Math.min(DropDownGeometryOptimizer.maxAvailableMediaWidthPx, availableMediaWidthPx);
        const newWidth: number = Math.min(maxDropDownWidthPx, availableMediaWidthPx);
        let newLeftOffset: number = $inputElement.offset().left - 2;
        if (newLeftOffset + newWidth > availableMediaWidthPx) {
            newLeftOffset = DropDownGeometryOptimizer.baseSpacingHorz + availableMediaWidthPx - newWidth - 2;
            // 2px: The border of the autocomplete-dropdown is 2px narrower than that of the input
        }
        $dropDownElement.css({
            "left": newLeftOffset + "px",
            "max-width": maxDropDownWidthPx + "px",
            "width": newWidth + "px",
        });
    }

    private showHandlers: {[inputElementId: string]: (event: JQueryEventObject) => any} = {};

    public getExperiments(): Experiment[] {
        return StripingConfiguration.getExperiments(configuration);
    }

    public handleDropDownShow($dropDownElement: JQuery, $inputElement: JQuery, context: IContext):
    JQueryPromise<undefined> {
        const deferred: JQueryDeferred<undefined> = $.Deferred();
        ExecutionHelper.shouldExecute(context, configuration)
            .done((shouldExecute: boolean): void => {
                if (shouldExecute) {
                    this.doHandleDropDownShow($dropDownElement, $inputElement);
                }
            })
            .always((): void => {
                deferred.resolve(undefined);
            });
        return deferred.promise();
    }

    public handleDropDownHide($dropDownElement: JQuery, $inputElement: JQuery, context: IContext):
    JQueryPromise<undefined> {
        const inputElementId: string = $inputElement.attr("id");
        const showHandler = inputElementId ? this.showHandlers[inputElementId] : undefined;
        if (showHandler) {
            $(window).off(DropDownGeometryOptimizer.resizeEventTypes, null, showHandler);
            delete this.showHandlers[inputElementId];
        }
        // Type assertion in the following line is required because, without it, some (but not all) compiler(s) may
        // incorrectly infer the type of the Deferred as JQueryPromise<{}> and raise an error.
        return ($.Deferred() as JQueryDeferred<undefined>).resolve(undefined).promise();
    }

    private doHandleDropDownShow($dropDownElement: JQuery, $inputElement: JQuery): void {
        const optimizeDropDownGeometry = (event: JQueryEventObject): any => {
            DropDownGeometryOptimizer.optimizeDropDownGeometry($dropDownElement, $inputElement);
        };
        optimizeDropDownGeometry(null);
        $(window).on(DropDownGeometryOptimizer.resizeEventTypes, null, null, optimizeDropDownGeometry);
        const inputElementId: string = $inputElement.attr("id");
        if (inputElementId) {
            this.showHandlers[$inputElement.attr("id")] = optimizeDropDownGeometry;
        }
    }
}
